import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { doc, setDoc } from 'firebase/firestore'
import { db } from '../config/firebase'
import {
  Trophy,
  Medal,
  Star,
  Search,
  Eye,
  Award,
  TrendingUp,
  Crown,
  BarChart3,
  Trash2,
  Monitor,
  BookOpen,
  Wifi,
  WifiOff,
  Download
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

const Leaderboard = () => {
  const [leaderboardData, setLeaderboardData] = useState([])
  const [weeklyData, setWeeklyData] = useState([])
  const [monthlyData, setMonthlyData] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [startRank, setStartRank] = useState('')
  const [endRank, setEndRank] = useState('')
  const [activeTab, setActiveTab] = useState('all') // 'all' | 'weekly' | 'monthly'
  const [deleteConfirm, setDeleteConfirm] = useState(null) // {userId, userName}
  const [deleting, setDeleting] = useState(false)
  const [clearConfirm, setClearConfirm] = useState(false) // For clearing entire leaderboard
  const [clearing, setClearing] = useState(false)
  const [rebuilding, setRebuilding] = useState(false)
  const { getLeaderboard, getUserTestResults, getUsers, updateDocument, deleteFromLeaderboard, updateLeaderboard } = useFirebase()

  // Helper function to convert Firestore timestamp
  const convertFirestoreTimestamp = (timestamp) => {
    if (!timestamp) return null
    if (timestamp instanceof Date) return timestamp
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate()
    }
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp)
    }
    return null
  }

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)

      console.log('🔄 Starting leaderboard data fetch...')

      // Fetch leaderboard data directly from Firestore
      const leaderboardData = await getLeaderboard()

      console.log('📊 Raw leaderboard data fetched:', leaderboardData)
      console.log('📊 Number of entries:', leaderboardData.length)

      if (leaderboardData.length === 0) {
        console.warn('⚠️ No leaderboard data found')
        setLeaderboardData([])
        setWeeklyData([])
        setMonthlyData([])
        return
      }


      // Debug: Log the first entry to see its structure
      if (leaderboardData.length > 0) {
        console.log('🔍 Sample leaderboard entry structure:', leaderboardData[0])
      }

      // Filter out anonymous/unregistered users (users without email or proper name)
      const registeredUsers = leaderboardData.filter(user => 
        user.email && user.email.trim() !== '' && 
        user.name && user.name.trim() !== '' && 
        user.name !== 'Anonymous User'
      );
      console.log(`🔍 Using ${registeredUsers.length} registered users from Firestore (filtered from ${leaderboardData.length} total)`)


      // Calculate time boundaries for weekly and monthly
      const now = new Date()

      // Start of current week (assuming Monday start)
      const startOfWeek = new Date(now)
      const currentDay = startOfWeek.getDay() // 0 (Sun) to 6 (Sat)
      const diff = startOfWeek.getDate() - currentDay + (currentDay === 0 ? -6 : 1) // Adjust to Monday
      startOfWeek.setDate(diff)
      startOfWeek.setHours(0, 0, 0, 0)

      // Start of current month
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      startOfMonth.setHours(0, 0, 0, 0)

      console.log('📅 Time boundaries for calculation:', {
        now: now.toISOString(),
        startOfWeek: startOfWeek.toISOString(),
        startOfMonth: startOfMonth.toISOString()
      })

      // Process each user to calculate weekly and monthly marks from their test results
      const usersWithMarks = await Promise.all(
        registeredUsers.map(async (user, index) => {
          try {
            // Get user's test results for weekly/monthly calculations
            const testResults = await getUserTestResults(user.email || user.id)

            // Detailed debug for top users (or specific user if debugging)
            const isDebugUser = index < 3;

            if (isDebugUser) {
              console.group(`🔍 Debugging Score: ${user.name} (${user.email})`)
              console.log('Total Online Tests:', testResults.online.length)
            }

            // Calculate weekly marks from online tests submitted in current week
            const weeklyMarks = testResults.online.reduce((sum, test) => {
              const testDate = convertFirestoreTimestamp(test.submitedDate || test.completedAt)
              const score = parseInt(test.score) || 0

              // loose comparison for inclusion (testDate >= startOfWeek)
              // We remove testDate <= now to avoid any timezone/clock skew issues for just-submitted tests
              const isIncluded = testDate && testDate >= startOfWeek

              if (isDebugUser && isIncluded) {
                console.log(`  ✅ WEEKLY Included: "${test.testName}" | Score: ${score} | Date: ${testDate?.toLocaleString()}`)
              } else if (isDebugUser && !isIncluded && testDate && testDate > new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)) {
                // Log recent exclusions to see if we missed something close
                console.log(`  ❌ WEEKLY Excluded: "${test.testName}" | Score: ${score} | Date: ${testDate?.toLocaleString()} | Reason: ${testDate < startOfWeek ? 'Before start of week' : 'Invalid date'}`)
              }

              if (isIncluded) {
                return sum + score
              }
              return sum
            }, 0)

            // Calculate monthly marks from online tests submitted in current month
            const monthlyMarks = testResults.online.reduce((sum, test) => {
              const testDate = convertFirestoreTimestamp(test.submitedDate || test.completedAt)
              const score = parseInt(test.score) || 0

              const isIncluded = testDate && testDate >= startOfMonth

              if (isDebugUser && isIncluded) {
                // console.log(`  ✅ MONTHLY Included: "${test.testName}" | Score: ${score} | Date: ${testDate?.toLocaleString()}`)
              }

              if (isIncluded) {
                return sum + score
              }
              return sum
            }, 0)

            // Calculate total online marks from all online tests for All-time accuracy
            const allTimeOnlineMarks = testResults.online.reduce((sum, test) => {
              const score = parseInt(test.score) || 0
              return sum + score
            }, 0)

            if (isDebugUser) {
              console.log(`📊 Calculated Totals -> All-time: ${allTimeOnlineMarks}, Weekly: ${weeklyMarks}, Monthly: ${monthlyMarks}`)
              console.groupEnd()
            }

            return {
              id: user.email || user.id,
              email: user.email || '',
              name: user.name || '',
              profileImage: user.profileImg || '',
              onlineTestMarks: allTimeOnlineMarks,
              offlineTestMarks: user.offlineTestMarks || 0,
              totalMarks: allTimeOnlineMarks,
              submitedDate: user.submitedDate || null,
              weeklyMarks: weeklyMarks,
              monthlyMarks: monthlyMarks,
              rank: user.rank || 0
            }
          } catch (error) {
            console.error(`❌ Error calculating marks for ${user.email}:`, error)
            return {
              id: user.email || user.id,
              email: user.email || '',
              name: user.name || '',
              onlineTestMarks: user.onlineTestMarks || 0,
              totalMarks: user.onlineTestMarks || 0,
              weeklyMarks: 0,
              monthlyMarks: 0,
              ...user
            }
          }
        })
      )



      console.log('✅ Calculated user marks from test results:', usersWithMarks)

      // Sort by online test marks (descending) for all-time leaderboard
      const sortedUsers = usersWithMarks
        .sort((a, b) => (b.onlineTestMarks || 0) - (a.onlineTestMarks || 0))

      // Sort by weekly marks (descending) for weekly leaderboard
      const sortedWeekly = [...usersWithMarks]
        .sort((a, b) => (b.weeklyMarks || 0) - (a.weeklyMarks || 0))

      // Sort by monthly marks (descending) for monthly leaderboard
      const sortedMonthly = [...usersWithMarks]
        .sort((a, b) => (b.monthlyMarks || 0) - (a.monthlyMarks || 0))

      console.log('📊 Sorting and filtering results:', {
        allTime: sortedUsers.slice(0, 3).map(u => ({ name: u.name, marks: u.onlineTestMarks })),
        weekly: sortedWeekly.slice(0, 3).map(u => ({ name: u.name, marks: u.weeklyMarks })),
        monthly: sortedMonthly.slice(0, 3).map(u => ({ name: u.name, marks: u.monthlyMarks })),
        totalFiltered: {
          allTime: usersWithMarks.length - sortedUsers.length,
          weekly: usersWithMarks.length - sortedWeekly.length,
          monthly: usersWithMarks.length - sortedMonthly.length
        }
      })

      // Update ranks based on online test marks and save back to Firestore (only for all-time)
      await updateLeaderboardRanks(sortedUsers)

      setLeaderboardData(sortedUsers)
      setWeeklyData(sortedWeekly)
      setMonthlyData(sortedMonthly)
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      toast.error('Failed to load leaderboard data')
    } finally {
      setLoading(false)
    }
  }

  const updateLeaderboardRanks = async (sortedUsers) => {
    try {
      // Update ranks based on current online test marks and save to Firestore
      const savePromises = sortedUsers.map(async (user, index) => {
        const rank = index + 1
        const leaderboardData = {
          email: user.email || "",
          name: user.name || "",
          offlineTestMarks: user.offlineTestMarks || 0,
          onlineTestMarks: user.onlineTestMarks || 0,
          profileImg: user.profileImage || user.profileImg || "",
          rank: rank,
          weeklyMarks: user.weeklyMarks || 0,
          monthlyMarks: user.monthlyMarks || 0,
          totalMarks: user.onlineTestMarks || 0, // Only online test marks
          submitedDate: user.submitedDate || null
        }

        // Use email as document ID (or user.id if email not available)
        const docId = user.email || user.id

        try {
          const leaderboardDocRef = doc(db, 'leaderboard', docId)
          await setDoc(leaderboardDocRef, leaderboardData, { merge: true })
          console.log(`✅ Updated rank for ${user.name} to #${rank}`)
        } catch (error) {
          console.error(`Error updating rank for ${user.name}:`, error)
        }
      })

      await Promise.all(savePromises)
      console.log('🏆 Leaderboard ranks updated in Firestore')
    } catch (error) {
      console.error('Error updating leaderboard ranks:', error)
      toast.error('Failed to update leaderboard ranks')
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return

    try {
      setDeleting(true)

      // Delete from Firestore leaderboard collection
      await deleteFromLeaderboard(deleteConfirm.userId)

      // Remove from local state
      setLeaderboardData(leaderboardData.filter(user => user.id !== deleteConfirm.userId))
      setWeeklyData(weeklyData.filter(user => user.id !== deleteConfirm.userId))
      setMonthlyData(monthlyData.filter(user => user.id !== deleteConfirm.userId))

      toast.success(`${deleteConfirm.userName || 'User'} removed from leaderboard successfully`)
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Error deleting user from leaderboard:', error)
      toast.error(`Failed to delete ${deleteConfirm.userName || 'user'} from leaderboard: ${error.message}`)
    } finally {
      setDeleting(false)
    }
  }

  const handleClearLeaderboard = async () => {
    try {
      setClearing(true)

      console.log('🗑️ Clearing entire leaderboard...')

      // Delete all users from leaderboard
      const deletePromises = leaderboardData.map(user =>
        deleteFromLeaderboard(user.id).catch(err => {
          console.error(`Failed to delete ${user.name}:`, err)
          return null
        })
      )

      await Promise.all(deletePromises)

      // Clear local state
      setLeaderboardData([])
      setWeeklyData([])
      setMonthlyData([])

      toast.success(`Successfully cleared ${leaderboardData.length} entries from leaderboard`)
      setClearConfirm(false)
    } catch (error) {
      console.error('Error clearing leaderboard:', error)
      toast.error(`Failed to clear leaderboard: ${error.message}`)
    } finally {
      setClearing(false)
    }
  }

  const handleRebuildLeaderboard = async () => {
    try {
      setRebuilding(true)
      toast.loading('Rebuilding leaderboard from user test results...')

      console.log('🔄 Rebuilding leaderboard...')

      // Call the updateLeaderboard function from FirebaseContext
      // This will fetch all users, calculate their scores, and update the leaderboard collection
      await updateLeaderboard()

      toast.dismiss()
      toast.success('Leaderboard rebuilt successfully!')

      // Reload the leaderboard data to show the new entries
      await loadData()
    } catch (error) {
      console.error('Error rebuilding leaderboard:', error)
      toast.dismiss()
      toast.error(`Failed to rebuild leaderboard: ${error.message}`)
    } finally {
      setRebuilding(false)
    }
  }

  const handleExport = () => {
    const currentData = activeTab === 'all' ? filteredData : (activeTab === 'weekly' ? filteredWeekly : filteredMonthly)

    if (currentData.length === 0) {
      toast.error('No data to export')
      return
    }

    // Prepare data for Excel
    const exportData = currentData.map((user, index) => ({
      'Rank': index + 1,
      'Name': user.name || 'N/A',
      'Email': user.email || 'N/A',
      'Online Test Marks': activeTab === 'all' ? (user.onlineTestMarks || 0)
        : activeTab === 'weekly' ? (user.weeklyMarks || 0)
          : (user.monthlyMarks || 0),
      'Offline Test Marks': user.offlineTestMarks || 0,
      'Total Marks': user.totalMarks || 0
    }))

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData)

    // Set column widths
    worksheet['!cols'] = [
      { wch: 8 },  // Rank
      { wch: 25 }, // Name
      { wch: 30 }, // Email
      { wch: 18 }, // Online Test Marks
      { wch: 18 }, // Offline Test Marks
      { wch: 15 }  // Total Marks
    ]

    // Create workbook
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Leaderboard')

    // Generate filename
    let filename = `leaderboard_${activeTab}`
    if (startRank && endRank) {
      filename += `_rank_${startRank}_to_${endRank}`
    } else if (startRank) {
      filename += `_rank_from_${startRank}`
    } else if (endRank) {
      filename += `_rank_until_${endRank}`
    }
    filename += '.xlsx'

    // Download file
    XLSX.writeFile(workbook, filename)
    toast.success(`Exported ${currentData.length} users successfully!`)
  }

  const filterByRank = (data) => {
    if (!startRank && !endRank) return data

    const start = parseInt(startRank) || 1
    const end = parseInt(endRank) || data.length

    // Since data is already sorted by marks, we just slice by rank positions
    return data.filter((user, index) => {
      const rank = index + 1
      return rank >= start && rank <= end
    })
  }

  const filteredData = filterByRank(leaderboardData.filter(user => {
    return user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  }))
  const filteredWeekly = filterByRank(weeklyData.filter(user => {
    return user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  }))
  const filteredMonthly = filterByRank(monthlyData.filter(user => {
    return user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  }))

  // Sort filtered data by rank or toppers score
  const sortedFilteredData = filteredData
  const sortedFilteredWeekly = filteredWeekly
  const sortedFilteredMonthly = filteredMonthly

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="h-6 w-6 text-yellow-500" />
      case 2:
        return <Medal className="h-6 w-6 text-gray-400" />
      case 3:
        return <Award className="h-6 w-6 text-orange-500" />
      default:
        return <span className="text-lg font-bold text-gray-600">#{rank}</span>
    }
  }

  const getRankBadge = (rank) => {
    if (rank <= 3) {
      const colors = {
        1: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white',
        2: 'bg-gradient-to-r from-gray-300 to-gray-500 text-white',
        3: 'bg-gradient-to-r from-orange-400 to-orange-600 text-white'
      }
      return colors[rank]
    }
    return 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-2xl shadow-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2 flex items-center">
              <Trophy className="h-8 w-8 mr-3" />
              Leaderboard Management
            </h1>
          </div>
          <button
            onClick={handleRebuildLeaderboard}
            disabled={rebuilding}
            className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rebuilding ? (
              <>
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Rebuilding...
              </>
            ) : (
              <>
                <TrendingUp className="h-5 w-5" />
                Rebuild Leaderboard
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="card p-4">
        <div className="grid grid-cols-3 gap-2">
          <button
            className={`btn w-full ${activeTab === 'all' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('all')}
          >All-time</button>
          <button
            className={`btn w-full ${activeTab === 'weekly' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('weekly')}
          >Weekly</button>
          <button
            className={`btn w-full ${activeTab === 'monthly' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('monthly')}
          >Monthly</button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col gap-4">
            {/* Search Input */}
            <div className="flex-1">
              <label htmlFor="search" className="form-label">
                Search Users
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  id="search"
                  placeholder="Search by name or email..."
                  className="input pl-10 w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Rank Filters and Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label htmlFor="startRank" className="form-label">
                  Start Rank:
                </label>
                <input
                  type="number"
                  id="startRank"
                  className="form-input"
                  placeholder="e.g., 1"
                  min="1"
                  value={startRank}
                  onChange={(e) => setStartRank(e.target.value)}
                />
              </div>

              <div className="flex-1">
                <label htmlFor="endRank" className="form-label">
                  End Rank:
                </label>
                <input
                  type="number"
                  id="endRank"
                  className="form-input"
                  placeholder="e.g., 100"
                  min="1"
                  value={endRank}
                  onChange={(e) => setEndRank(e.target.value)}
                />
              </div>

              <div className="flex items-end space-x-2">
                <button
                  onClick={handleExport}
                  className="btn-primary flex items-center gap-2 whitespace-nowrap"
                  disabled={
                    (activeTab === 'all' ? filteredData :
                      activeTab === 'weekly' ? filteredWeekly :
                        filteredMonthly).length === 0
                  }
                >
                  <Download className="h-4 w-4" />
                  Export
                </button>
                <button
                  onClick={() => setClearConfirm(true)}
                  className="btn-outline whitespace-nowrap"
                  disabled={clearing || leaderboardData.length === 0}
                >
                  {clearing ? 'Clearing...' : 'Clear All Data'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top 3 Cards for active tab */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Crown className="h-5 w-5 mr-2 text-yellow-500" />
            {activeTab === 'all' ? 'All-time Toppers'
              : activeTab === 'weekly' ? `Weekly Toppers (${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Week)`
                : `Monthly Toppers (${new Date().toLocaleDateString('en-US', { month: 'short' })})`}
          </h3>
          <p className="text-sm text-gray-600">
            {activeTab === 'all' ? 'Leading performers in nursing examinations'
              : activeTab === 'weekly' ? 'Score accumulation starting from Monday of this week'
                : 'Score accumulation starting from the 1st of this month'}
          </p>
        </div>
        <div className="card-body">
          {loading ? (
            <SkeletonTable rows={10} columns={6} />
          ) : (() => {
            const currentData = activeTab === 'all' ? sortedFilteredData : (activeTab === 'weekly' ? sortedFilteredWeekly : sortedFilteredMonthly)
            if (!currentData || currentData.length === 0) {
              return (
                <div className="text-center py-8">
                  <Trophy className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Top Performers Yet</h3>
                  <p className="text-gray-600">Not enough activity to display toppers</p>
                </div>
              )
            }
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {currentData.slice(0, 3).map((user, index) => {
                  const rank = index + 1
                  const rankColors = {
                    1: { bg: 'bg-gradient-to-br from-yellow-400 to-yellow-600', text: 'text-white', icon: 'text-yellow-200' },
                    2: { bg: 'bg-gradient-to-br from-gray-300 to-gray-500', text: 'text-white', icon: 'text-gray-200' },
                    3: { bg: 'bg-gradient-to-br from-orange-400 to-orange-600', text: 'text-white', icon: 'text-orange-200' }
                  }
                  const colors = rankColors[rank]

                  return (
                    <div key={user.id} className={`${colors.bg} rounded-xl p-6 ${colors.text} relative overflow-hidden min-h-64 flex flex-col`}>
                      <div className="absolute top-0 right-0 opacity-10">
                        {getRankIcon(rank)}
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          {getRankIcon(rank)}
                          <span className="ml-2 text-lg font-bold">#{rank}</span>
                        </div>
                        <div className={`w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center`}>
                          <Trophy className={`h-4 w-4 ${colors.icon}`} />
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={user.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-lg font-bold">
                              {user.name?.charAt(0) || 'U'}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-lg truncate">
                            {user.name || 'Anonymous User'}
                          </h4>
                          <p className="text-sm opacity-80 truncate">
                            {user.email}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3 mt-auto">
                        <div className="flex items-center justify-between">
                          <span className="text-sm opacity-90">{activeTab === 'all' ? 'Online Score' : activeTab === 'weekly' ? 'Weekly Online Score' : 'Monthly Online Score'}</span>
                          <div className="flex items-center">
                            <Star className="h-4 w-4 mr-1" />
                            <span className="font-bold text-lg">{
                              activeTab === 'all' ? ((user.onlineTestMarks) || 0)
                                : activeTab === 'weekly' ? (user.weeklyMarks || 0)
                                  : (user.monthlyMarks || 0)
                            }</span>
                          </div>
                        </div>
                        {activeTab === 'all' ? (
                          <div className="text-center mt-auto">
                            <div className="bg-white bg-opacity-10 rounded-lg p-2">
                              <span className="text-sm opacity-80">Online Test Marks</span>
                              <div className="font-semibold text-center">{user.onlineTestMarks || 0}</div>
                            </div>
                          </div>
                        ) : (
                          /* placeholder to equalize height across tabs */
                          <div className="grid grid-cols-2 gap-3 text-xs opacity-0 select-none pointer-events-none">
                            <div className="bg-white bg-opacity-10 rounded-lg p-2">
                              <div className="flex items-center justify-center mb-1"><span>_</span></div>
                              <div className="font-semibold text-center">0</div>
                            </div>
                            <div className="bg-white bg-opacity-10 rounded-lg p-2">
                              <div className="flex items-center justify-center mb-1"><span>_</span></div>
                              <div className="font-semibold text-center">0</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })()}
        </div>
      </div>

      {/* Tabular Leaderboard */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            {activeTab === 'all' ? 'All-time Leaderboard' : activeTab === 'weekly' ? 'Weekly Leaderboard' : 'Monthly Leaderboard'}
          </h3>
        </div>
        <div className="card-body p-0">
          {(() => {
            const currentData = activeTab === 'all' ? sortedFilteredData : (activeTab === 'weekly' ? sortedFilteredWeekly : sortedFilteredMonthly)
            if (!currentData || currentData.length === 0) return null
            return (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>User</th>
                      <th>{activeTab === 'all' ? 'Online Test Marks' : activeTab === 'weekly' ? 'Weekly Online Marks' : 'Monthly Online Marks'}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Remaining */}
                    {currentData.slice(3).map((user, index) => {
                      const rank = index + 4
                      return (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td>
                            <div className="flex items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRankBadge(rank)}`}>
                                {rank <= 3 ? (
                                  getRankIcon(rank)
                                ) : (
                                  <span className="text-sm font-bold">#{rank}</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                {user.profileImage ? (
                                  <img
                                    src={user.profileImage}
                                    alt={user.name}
                                    className="w-10 h-10 rounded-full object-cover"
                                  />
                                ) : (
                                  <span className="text-sm font-bold text-gray-600">
                                    {user.name?.charAt(0) || 'U'}
                                  </span>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium text-gray-900 truncate">
                                  {user.name || 'Anonymous User'}
                                </h4>
                                <p className="text-sm text-gray-600 truncate">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-500 mr-1" />
                              <span className="font-semibold text-gray-900">{
                                activeTab === 'all' ? (user.onlineTestMarks || 0)
                                  : activeTab === 'weekly' ? (user.weeklyMarks || 0)
                                    : (user.monthlyMarks || 0)
                              }</span>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center space-x-2">
                              <Link
                                to={`/users/view/${user.id}`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Profile"
                              >
                                <Eye className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDeleteConfirm({ userId: user.id, userName: user.name })
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Delete from leaderboard"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          })()}
          {((activeTab === 'all' ? sortedFilteredData : (activeTab === 'weekly' ? sortedFilteredWeekly : sortedFilteredMonthly))).length === 0 && (
            <div className="text-center py-12">
              <Trophy className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No participants found</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Try adjusting your search criteria' : 'No users have activity yet'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {
        deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <Trash2 className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Delete from Leaderboard</h3>
                    <p className="text-red-100 text-sm">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="mb-6">
                  <p className="text-gray-700 text-base mb-4">
                    Are you sure you want to remove <span className="font-bold text-gray-900">{deleteConfirm.userName || 'this user'}</span> from the leaderboard?
                  </p>
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-800">
                          This will permanently remove their entry from the Firestore leaderboard collection.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {deleting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        Delete User
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Clear All Leaderboard Confirmation Modal */}
      {
        clearConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <Trash2 className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Clear Entire Leaderboard</h3>
                    <p className="text-red-100 text-sm">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6">
                <div className="mb-6">
                  <p className="text-gray-700 text-base mb-4">
                    Are you sure you want to clear the <span className="font-bold text-gray-900">entire leaderboard</span>?
                  </p>
                  <p className="text-gray-700 text-base mb-4">
                    This will permanently delete <span className="font-bold text-red-600">{leaderboardData.length} entries</span> from the Firestore leaderboard collection.
                  </p>
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800 font-semibold">
                          ⚠️ WARNING: This will delete ALL user rankings and scores permanently!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setClearConfirm(false)}
                    disabled={clearing}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClearLeaderboard}
                    disabled={clearing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {clearing ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Clearing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" />
                        Clear All Data
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

    </div >
  )
}

export default Leaderboard
